/**
 * Change History Slice
 *
 * Redux slice for managing entity change history state.
 * Handles fetching, caching, filtering, and diff selection for change history entries.
 */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchChangeHistoryAPI } from 'src/services/api/changeHistoryService';
import {
  ChangeHistoryEntry,
  ChangeHistoryParams,
  ChangeHistoryState,
} from 'src/types/change-history/changeHistory.types';
import { logger } from 'src/utils/logger';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Creates a unique entity key for caching change history entries
 */
export const createEntityKey = (entityType: string, entityId: number): string => {
  return `${entityType}-${entityId}`;
};

// ============================================================================
// Initial State
// ============================================================================

const initialState: ChangeHistoryState = {
  entriesByEntity: {},
  loading: {},
  errors: {},
  hasFetched: {},
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetches change history for an entity
 */
export const fetchChangeHistory = createAsyncThunk(
  'changeHistory/fetchChangeHistory',
  async (params: ChangeHistoryParams, { rejectWithValue }) => {
    try {
      const response = await fetchChangeHistoryAPI(params);
      return {
        entityKey: createEntityKey(params.entity_type, params.entity_id),
        entries: response.entries,
        totalCount: response.total_count,
        hasMore: response.has_more,
      };
    } catch (error) {
      logger.error('Failed to fetch change history', { error, params });
      return rejectWithValue((error as Error).message || 'Failed to fetch change history');
    }
  }
);

// ============================================================================
// Slice Definition
// ============================================================================

const changeHistorySlice = createSlice({
  name: 'changeHistory',
  initialState,
  reducers: {
    clearEntityHistory: (state, action: PayloadAction<string>) => {
      const entityKey = action.payload;
      delete state.entriesByEntity[entityKey];
      delete state.loading[entityKey];
      delete state.errors[entityKey];
      delete state.hasFetched[entityKey];
    },

    /**
     * Resets the entire change history state
     */
    resetChangeHistory: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChangeHistory.pending, (state, action) => {
        const entityKey = createEntityKey(action.meta.arg.entity_type, action.meta.arg.entity_id);
        state.loading[entityKey] = true;
        state.errors[entityKey] = null;
      })
      .addCase(fetchChangeHistory.fulfilled, (state, action) => {
        const { entityKey, entries } = action.payload;
        // Skip if the entity was cleared while this request was in-flight
        if (!(entityKey in state.loading)) return;
        state.loading[entityKey] = false;
        state.entriesByEntity[entityKey] = entries;
        state.hasFetched[entityKey] = true;
        state.errors[entityKey] = null;
      })
      .addCase(fetchChangeHistory.rejected, (state, action) => {
        const entityKey = createEntityKey(action.meta.arg.entity_type, action.meta.arg.entity_id);
        // Skip if the entity was cleared while this request was in-flight
        if (!(entityKey in state.loading)) return;
        state.loading[entityKey] = false;
        state.errors[entityKey] = action.payload as string;
        state.hasFetched[entityKey] = true;
      });
  },
});

// ============================================================================
// Selectors
// ============================================================================

/**
 * Selects change history entries for a specific entity
 */
export const selectChangeHistoryEntries = (
  state: { changeHistory: ChangeHistoryState },
  entityKey: string
): ChangeHistoryEntry[] => {
  return state.changeHistory.entriesByEntity[entityKey] || [];
};

/**
 * Selects loading state for a specific entity
 */
export const selectChangeHistoryLoading = (
  state: { changeHistory: ChangeHistoryState },
  entityKey: string
): boolean => {
  return state.changeHistory.loading[entityKey] || false;
};

/**
 * Selects error state for a specific entity
 */
export const selectChangeHistoryError = (
  state: { changeHistory: ChangeHistoryState },
  entityKey: string
): string | null => {
  return state.changeHistory.errors[entityKey] || null;
};

/**
 * Selects whether data has been fetched for a specific entity
 */
export const selectChangeHistoryHasFetched = (
  state: { changeHistory: ChangeHistoryState },
  entityKey: string
): boolean => {
  return state.changeHistory.hasFetched[entityKey] || false;
};

// ============================================================================
// Exports
// ============================================================================

export const { clearEntityHistory, resetChangeHistory } = changeHistorySlice.actions;

export default changeHistorySlice.reducer;
